import $bus from '~/eventBus.js';

/**
 * Freezes a self-updating feed while the pointer rests over it (or while something
 * inside it is focused), so a row stays put long enough to be read and clicked.
 *
 * Rows keep arriving into the host component's own queue meanwhile and are released
 * when the pointer leaves or the visitor clicks the badge above the table.
 *
 * The feed is also held, silently, while the search results are open: iOS Safari drops
 * a tap on a search result when a new row with links shows up at that moment.
 *
 * The host component is expected to:
 *   - bail out of its drip handler while isFeedPaused or isFeedHeld is true;
 *   - report every batch it receives to trackFeedArrivals(), which is what the badge counts;
 *   - implement flushFeedQueue(), which applies everything that has piled up.
 */
export default {
    data() {
        return {
            isFeedHovered: false,
            isFeedFocused: false,
            feedPendingCount: 0,
            isFeedHeld: false,
        };
    },

    created() {
        $bus.$on('search-results-toggle', this.onSearchResultsToggle);
    },

    beforeDestroy() {
        $bus.$off('search-results-toggle', this.onSearchResultsToggle);
    },

    computed: {
        isFeedPaused() {
            return this.isFeedHovered || this.isFeedFocused;
        },
    },

    watch: {
        isFeedPaused(isPaused) {
            if (isPaused) {
                this.feedPendingCount = 0;
            }
        },
    },

    methods: {
        onFeedPointerEnter() {
            // A tap on a touchscreen emits mouseenter that never gets its matching
            // mouseleave, which would leave the feed frozen for good:
            if (matchMedia('(hover: hover)').matches) {
                this.isFeedHovered = true;
            }
        },

        onFeedPointerLeave() {
            this.isFeedHovered = false;
            this.resumeFeed();
        },

        onSearchResultsToggle(isOpened) {
            this.isFeedHeld = isOpened;
        },

        onFeedFocusIn() {
            this.isFeedFocused = true;
        },

        onFeedFocusOut() {
            this.isFeedFocused = false;
            this.resumeFeed();
        },

        trackFeedArrivals(count) {
            if (this.isFeedPaused) {
                this.feedPendingCount += count;
            }
        },

        showFeedQueue() {
            this.flushFeedQueue();
            this.feedPendingCount = 0;
        },

        resumeFeed() {
            if (!this.isFeedPaused) {
                this.showFeedQueue();
            }
        },
    },
};
